import { createContext, useEffect, useReducer, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

// reducer
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import axios from 'utils/axios';
import { BACKEND_URL } from '@/constants/globalConstants';
import { getMappedOrganizations, LOGIN_SUBROOT_KEY, getSubRootName, setLoginSubroot } from '@/utils/utils';

// ==============================|| INITIAL STATE ||============================== //

const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

const backendBaseURL = BACKEND_URL;

// ==============================|| HELPERS ||============================== //

const verifyToken = (sessionToken) => {
    if (!sessionToken) {
        return false;
    }

    try {
        const decoded = jwtDecode(sessionToken);

        return !!decoded.exp && decoded.exp > Date.now() / 1000;
    } catch (error) {
        console.error('Invalid JWT token:', error);
        return false;
    }
};

const normalizeOrganizationName = (value = '') =>
    decodeURIComponent(value)
        ?.split(/\s+/)
        .filter(Boolean)
        .map((word, index) => word.length === 1 || word === word.toUpperCase() || index === 0
            ? word.toLowerCase()
            : word.charAt(0).toUpperCase() + word.slice(1))
        .join('');

const getCurrentSubroot = () => (getSubRootName());

const getUserOrganizations = (user) => {
    if (!user) {
        return [];
    }

    if (Object.hasOwn(user, 'tenant_organizations') && user.tenant_organizations) {
        const { tenant_organizations: tenantOrganizations } = user;
        const result = Array.isArray(tenantOrganizations) ? tenantOrganizations : [tenantOrganizations];
        return result;
    }

    if (user.organization) {
        const result = Array.isArray(user.organization) ? user.organization : [user.organization];
        return result;
    }

    return [];
};

const getVisibleOrganizations = (user) =>
    getUserOrganizations(user)
    .filter((o) => o.name != 'super-admin')
    .map((o) => ({
        mapped_name: normalizeOrganizationName(o.name),
        // shown on the cards of the organization selection (the same shape of the /auth/login response)
        original_name: o.name
    }));

/**
 * Cerca l'organizzazione corrispondente alla subroot indicata.
 * La subroot deve essere quella ricavata dall'URL e normalizzata.
 */
const getOrganizationForSubroot = (user, subroot) => {
    if (!subroot) {
        return null;
    }

    const organizations = getUserOrganizations(user);

    return (
        organizations.find(
            (organization) =>
                normalizeOrganizationName(organization?.name) === subroot
        ) ?? null
    );
};

/**
 * Salvataggio/cleanup della sessione.
 */
const setSession = (sessionToken, user = null) => {
    if (sessionToken) {
        localStorage.setItem('sessionToken', sessionToken);

        if (user) {
            localStorage.setItem('authUser', JSON.stringify(user));
        }

        localStorage.setItem('isAuthenticated', 'true');

        axios.defaults.headers.common.Authorization =
            `Bearer ${sessionToken}`;
    } else {
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem(LOGIN_SUBROOT_KEY);

        delete axios.defaults.headers.common.Authorization;
    }
};

// ==============================|| JWT CONTEXT ||============================== //

const DirectJWTContext = createContext(null);

// ==============================|| JWT PROVIDER ||============================== //

export const DirectJWTProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // ==============================|| INITIALIZATION ||============================== //

    useEffect(() => {
        const init = async () => {
            try {
                const sessionToken =
                    window.localStorage.getItem('sessionToken');

                const storedUser =
                    window.localStorage.getItem('authUser');

                if (
                    !sessionToken ||
                    !storedUser ||
                    !verifyToken(sessionToken)
                ) {
                    setSession(null);

                    dispatch({
                        type: LOGOUT
                    });

                    return;
                }

                const user = JSON.parse(storedUser);

                /**
                 * La subroot usata per il controllo e' quella presente nell'URL.
                 * Esempio: /legaserieb/dashboard -> legaserieb
                 */
                const organization = getCurrentSubroot();
                const activeOrganization =
                    getOrganizationForSubroot(user, organization);

                if (!activeOrganization) {
                    console.warn(
                        `Organization "${organization}" is not available for user`,
                        user
                    );

                    setSession(null);

                    dispatch({
                        type: LOGOUT
                    });

                    return;
                }

                // Solo dopo che il controllo ha avuto successo memorizziamo
                // la stessa subroot usata per validare l'organizzazione.
                setLoginSubroot(organization && `/${organization}`);
                setSession(sessionToken, user);
                dispatch({
                    type: LOGIN,
                    payload: {
                        isLoggedIn: true,
                        user: { ...user, visibleOrganizations: getMappedOrganizations(getVisibleOrganizations(user)) }
                    }
                });
            } catch (error) {
                console.error(
                    'Error initializing authentication:',
                    error
                );

                setSession(null);

                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    // ==============================|| LOGIN ||============================== //

    // Utente e token di un login da /login in attesa della scelta dell'organizzazione
    const pendingLoginRef = useRef(null);

    // Valida l'organizzazione, apre la sessione e aggiorna lo stato
    const openSession = (accessToken, user, organization) => {
        const activeOrganization =
            getOrganizationForSubroot(user, organization);

        if (!activeOrganization) {
            console.warn(
                `Organization "${organization}" is not available for user`,
                user
            );

            throw new Error(
                'You do not have access to this organization'
            );
        }

        // Il valore viene salvato SOLO dopo che l'organizzazione e' stata validata.
        setLoginSubroot(organization && `/${organization}`);

        setSession(accessToken, user);

        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                user: { ...user, visibleOrganizations: getMappedOrganizations(getVisibleOrganizations(user)) }
            }
        });
    };

    // Secondo passo di un login da /login: organization e' il mapped_name scelto
    const selectOrganization = async (pendingToken, organization) => {
        const pending = pendingLoginRef.current;

        if (!pending || pending.accessToken !== pendingToken) {
            throw new Error('Login session expired, please sign in again');
        }

        openSession(pending.accessToken, pending.user, organization);
        pendingLoginRef.current = null;
    };

    const login = async (email, password) => {
        try {
            /**
             * Login sempre diretto.
             *
             * Nessun pending_token.
             * Nessuna selectOrganization.
             * Nessuna organizzazione inviata dal frontend.
             */
            const response = await axios.post(
                `/v1/api/login`,
                {
                    email,
                    password
                }
            );

            const {
                access_token: accessToken,
                user
            } = response.data;

            if (!accessToken || !user) {
                throw new Error(
                    'Invalid login response: access_token or user missing'
                );
            }

            if (!verifyToken(accessToken)) {
                throw new Error(
                    'The server returned an invalid or expired access token'
                );
            }

            /**
             * Controllo tenant.
             *
             * SUPER ADMIN:
             *
             * tenant_organizations:
             * [
             *   { name: "super-admin" },
             *   { name: "Lega Serie B" },
             *   { name: "FIGC" }
             * ]
             *
             * /legaserieb -> trova "Lega Serie B"
             *
             *
             * UTENTE NORMALE:
             *
             * organization:
             * {
             *   name: "Lega Serie B"
             * }
             *
             * /legaserieb -> trova "Lega Serie B"
             */
                // La subroot corrente e' sia il valore usato per il controllo
                // sia quello che verra' memorizzato tramite setLoginSubroot.
            const organization = getCurrentSubroot();

            if (!organization) {
                const organizations = getVisibleOrganizations(user);

                if (organizations.length > 1) {
                    pendingLoginRef.current = { accessToken, user };

                    return { pendingToken: accessToken, organizations };
                }

                if (organizations.length === 0) {
                    throw new Error(
                        'You do not have access to any organization'
                    );
                }

                openSession(accessToken, user, organizations[0].mapped_name);
                return null;
            }

            openSession(accessToken, user, organization);
            return null;
        } catch (error) {
            setSession(null);

            dispatch({
                type: LOGOUT
            });

            throw error;
        }
    };

    // ==============================|| LOGOUT ||============================== //

    const logout = async () => {
        try {
            /**
             * Manteniamo lo stesso comportamento del JWTContext originale.
             *
             * Se il nuovo backend non espone /auth/logout,
             * questa chiamata può essere eliminata senza cambiare
             * il cleanup locale sotto.
             */
            await axios.post(`/auth/logout`);
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            setSession(null);

            dispatch({
                type: LOGOUT
            });
        }
    };

    // ==============================|| LOCAL LOGOUT ||============================== //

    const localLogout = async () => {
        setSession(null);

        dispatch({
            type: LOGOUT
        });
    };

    // ==============================|| AUTHORIZATION HELPERS ||============================== //

    const checkUserRoles = (roles) => {
        return state.user?.roles?.some((userRole) =>
            roles.includes(userRole.name)
        );
    };

    const checkUserPermissions = (requiredPermissions) => {
        const userPermissions = new Set();
        const user = state.user;

        if (user && Array.isArray(user.roles)) {
            user.roles.forEach((role) => {
                if (Array.isArray(role.permissions)) {
                    role.permissions.forEach((permission) =>
                        userPermissions.add(permission)
                    );
                }
            });
        }

        return requiredPermissions.every((permission) =>
            userPermissions.has(permission)
        );
    };

    // ==============================|| COMPATIBILITY METHODS ||============================== //

    const resetPassword = async (email) => {
        // Implementare se previsto dal nuovo backend.
    };

    const updateProfile = () => {};

    // ==============================|| LOADER ||============================== //

    if (
        state.isInitialized !== undefined &&
        !state.isInitialized
    ) {
        return <Loader />;
    }

    // ==============================|| PROVIDER ||============================== //

    return (
        <DirectJWTContext
            value={{
                ...state,

                login,
                selectOrganization,
                logout,
                localLogout,

                resetPassword,
                updateProfile,

                checkUserRoles,
                checkUserPermissions
            }}
        >
            {children}
        </DirectJWTContext>
    );
};

export default DirectJWTContext;